import React, { Component } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { captureException } from '../observability/sentry';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary that catches unhandled React rendering errors and
 * shows a themed fallback UI with a "Reload App" button. Errors are forwarded
 * to the (currently no-op) observability stub for future retrofit.
 */
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureException(error, {
      context: 'AppErrorBoundary',
      componentStack: info.componentStack ?? '',
    });
  }

  handleReload = (): void => {
    if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      // Reset error state — Expo Router will re-render the current route.
      // For a full restart, expo-updates reloadAsync() can be added later.
      this.setState({ hasError: false });
    }
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <Text style={styles.icon}>{'\u26A0\uFE0F'}</Text>
          <Text style={styles.title}>Something went wrong</Text>
          <Text style={styles.message}>
            An unexpected error occurred. The error has been reported automatically.
          </Text>
          <TouchableOpacity
            style={styles.button}
            onPress={this.handleReload}
            accessibilityRole="button"
            accessibilityLabel="Reload App"
            testID="error-boundary.reload-button"
          >
            <Text style={styles.buttonText} accessible={false}>Reload App</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    paddingHorizontal: 24,
  },
  icon: {
    fontSize: 48,
    marginBottom: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  message: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  button: {
    backgroundColor: '#3b82f6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 15,
  },
});
